import { Fragment, type CSSProperties, type ReactNode } from "react";
import { useEmbedTokens } from "../theme";
import type { TokenSet } from "../tokens";
import { parseMarkdown, type MdBlock, type MdInline } from "../markdown";

// EmbedMarkdown — renders a markdown product description for the customer
// embed, styled entirely from theme tokens because the embed mounts inside
// third-party Framer sites where none of our stylesheets exist.
//
// It emits React ELEMENTS from the parsed tree; source text is only ever a
// text child or a scheme-checked href. Nothing here builds an HTML string, so
// markup injection is structurally impossible rather than filtered — do not
// "simplify" any branch to dangerouslySetInnerHTML.

interface Props {
  /** Raw markdown. Null/undefined/blank renders nothing. */
  source: string | null | undefined;
}

/**
 * Description headings render one level below the surrounding page heading
 * (the product name is an <h2>), so `#` → <h3> and the outline stays valid.
 * Levels are clamped to 3 by the parser, so this never runs past <h5>.
 */
const HEADING_TAGS = { 1: "h3", 2: "h4", 3: "h5" } as const;

function headingStyle(t: TokenSet, level: 1 | 2 | 3, isFirst: boolean): CSSProperties {
  const size = level === 1 ? t.fontSize.lg : level === 2 ? t.fontSize.md : t.fontSize.sm;
  return {
    fontSize: size,
    fontWeight: 700,
    color: t.colors.text,
    lineHeight: t.lineHeight.tight,
    // A description almost always opens with a heading; a top margin there
    // would double the gap the parent already set.
    margin: `${isFirst ? 0 : t.spacing.md}px 0 ${t.spacing.xs}px 0`,
  };
}

function renderInline(nodes: MdInline[], t: TokenSet): ReactNode {
  return nodes.map((node, i) => {
    switch (node.kind) {
      case "text":
        return <Fragment key={i}>{node.value}</Fragment>;
      case "strong":
        return (
          <strong key={i} style={{ fontWeight: 700, color: t.colors.text }}>
            {renderInline(node.children, t)}
          </strong>
        );
      case "em":
        return (
          <em key={i} style={{ fontStyle: "italic" }}>
            {renderInline(node.children, t)}
          </em>
        );
      case "code":
        return (
          <code
            key={i}
            style={{
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: "0.9em",
              background: t.colors.surfaceSunken,
              borderRadius: t.borderRadiusSm,
              padding: "1px 4px",
            }}
          >
            {node.value}
          </code>
        );
      case "link":
        return (
          <a
            key={i}
            href={node.href}
            // The embed lives on a customer's own page, so a link must not
            // navigate their visitor away from it, and noopener denies the
            // opened tab a window.opener handle back into their document.
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: t.colors.primary, textDecoration: "underline" }}
          >
            {renderInline(node.children, t)}
          </a>
        );
    }
  });
}

function renderBlock(block: MdBlock, index: number, t: TokenSet): ReactNode {
  const bodyStyle: CSSProperties = {
    fontSize: t.fontSize.md,
    color: t.colors.textSecondary,
    lineHeight: t.lineHeight.normal,
  };

  switch (block.kind) {
    case "heading": {
      const Tag = HEADING_TAGS[block.level];
      return (
        <Tag key={index} style={headingStyle(t, block.level, index === 0)}>
          {renderInline(block.children, t)}
        </Tag>
      );
    }
    case "paragraph":
      return (
        <p key={index} style={{ ...bodyStyle, margin: `0 0 ${t.spacing.sm}px 0` }}>
          {renderInline(block.children, t)}
        </p>
      );
    case "list": {
      const Tag = block.ordered ? "ol" : "ul";
      return (
        <Tag
          key={index}
          style={{ ...bodyStyle, margin: `0 0 ${t.spacing.sm}px 0`, paddingLeft: t.spacing.lg }}
        >
          {block.items.map((item, i) => (
            <li key={i} style={{ marginBottom: t.spacing.xs }}>
              {renderInline(item, t)}
            </li>
          ))}
        </Tag>
      );
    }
  }
}

export function EmbedMarkdown({ source }: Props) {
  const t = useEmbedTokens();
  const blocks = parseMarkdown(source ?? "");
  if (blocks.length === 0) return null;
  return <div>{blocks.map((block, i) => renderBlock(block, i, t))}</div>;
}
